import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicProduct } from '../lib/api';
import type { ProductWithVariants, ProductVariant } from '../../shared/types';
import { useCartStore } from '../store/useCartStore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import {
  Loader2,
  ImageIcon,
  Minus,
  Plus,
  ShoppingCart,
  CreditCard,
  Check,
  ChevronLeft,
  ZoomIn,
  X,
  Heart,
  Share2,
  Truck,
  ShieldCheck,
  Play,
} from 'lucide-react';
import VideoEmbed from '../components/VideoEmbed';
import DirectVideoPlayer from '../components/DirectVideoPlayer';
import ResponsiveImage from '../components/ResponsiveImage';
import { isVideoUrl, parseVideoUrl } from '../lib/video';
import { formatPrice } from '../lib/formatPrice';
import { usePaymentSettings } from '../lib/usePaymentSettings';


type DetailTab = 'photos' | 'description' | 'care' | 'usage';
type Lang = 'zh-HK' | 'zh-CN' | 'en';

function getLocalized(product: ProductWithVariants, lang: Lang, field: 'name' | 'subtitle' | 'description' | 'careInfo' | 'usage'): string {
  const map: Record<string, Record<string, keyof ProductWithVariants>> = {
    name: { 'zh-HK': 'nameZh', 'zh-CN': 'nameCn', en: 'nameEn' },
    subtitle: { 'zh-HK': 'subtitleZh', 'zh-CN': 'subtitleCn', en: 'subtitleEn' },
    description: { 'zh-HK': 'descriptionZh', 'zh-CN': 'descriptionCn', en: 'descriptionEn' },
    careInfo: { 'zh-HK': 'careInfoZh', 'zh-CN': 'careInfoCn', en: 'careInfoEn' },
    usage: { 'zh-HK': 'usageZh', 'zh-CN': 'usageCn', en: 'usageEn' },
  };
  const key = map[field][lang];
  const value = product[key];
  if (typeof value === 'string' && value.trim()) return value;
  // Fallback chain
  const fallbacks: (keyof ProductWithVariants)[] =
    lang === 'zh-HK'
      ? [map[field]['zh-CN'], map[field].en]
      : lang === 'zh-CN'
      ? [map[field]['zh-HK'], map[field].en]
      : [map[field].en, map[field]['zh-HK'], map[field]['zh-CN']];
  for (const fb of fallbacks) {
    const v = product[fb];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

function getVariantName(variant: ProductVariant, lang: Lang): string {
  const map: Record<Lang, keyof ProductVariant> = { 'zh-HK': 'nameZh', 'zh-CN': 'nameCn', en: 'nameEn' };
  const value = variant[map[lang]];
  if (typeof value === 'string' && value.trim()) return value;
  return variant.nameCn || variant.nameEn || variant.nameZh || variant.sku;
}

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const addItem = useCartStore((s) => s.addItem);
  const { settings: paymentSettings } = usePaymentSettings();
  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  const [product, setProduct] = useState<ProductWithVariants | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('photos');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [demoVideoUrl, setDemoVideoUrl] = useState<string | null>(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [longImageOpen, setLongImageOpen] = useState(false);

  const lang = (i18n.language as Lang) || 'zh-HK';

  useEffect(() => {
    async function load() {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getPublicProduct(slug);
        if (!res.success || !res.data) {
          throw new Error(res.error?.message || 'Product not found');
        }
        setProduct(res.data);
        setSelectedImage(res.data.imageUrl || res.data.gallery?.[0] || '');
        if (res.data.variants?.length > 0) {
          setSelectedVariant(res.data.variants[0]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  // Allow native pinch-to-zoom on mobile only on product detail pages
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport?.getAttribute('content') || '';
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, viewport-fit=cover'
      );
    }
    return () => {
      if (viewport && originalContent) {
        viewport.setAttribute('content', originalContent);
      }
    };
  }, []);

  const allImages = product
    ? Array.from(
        new Set([product.imageUrl, ...(product.gallery || [])].filter((u): u is string => !!u))
      )
    : [];

  const effectivePrice = product
    ? product.price + (selectedVariant?.priceAdjustment || 0)
    : 0;

  const availableStock = product
    ? selectedVariant
      ? selectedVariant.stock
      : product.stockQuantity
    : 0;
  const isOutOfStock = availableStock <= 0;

  const handleAddToCart = (checkout: boolean) => {
    if (!product || isOutOfStock) return;
    if (quantity > availableStock) {
      alert(t('products.insufficient_stock') || '庫存不足');
      return;
    }
    if (!product) return;
    const name = getLocalized(product, lang, 'name');
    addItem({
      productId: product.id,
      name: selectedVariant ? `${name} - ${getVariantName(selectedVariant, lang)}` : name,
      price: effectivePrice,
      image: product.imageUrl || '',
      variantId: selectedVariant?.id,
      quantity,
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
    if (checkout) {
      navigate('/checkout');
    }
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <Header />
        <main className="pt-24 pb-20 px-4 flex items-center justify-center min-h-[60vh]">
          <div className="text-center text-brand-ink/50 text-lg">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            載入產品中…
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <Header />
        <main className="pt-24 pb-20 px-4">
          <div className="max-w-2xl mx-auto text-center py-16">
            <h1 className="text-2xl font-serif text-brand-ink mb-4">找不到產品</h1>
            <p className="text-brand-ink/60 mb-8">{error || '產品不存在或已下架'}</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-cinnabar text-brand-paper text-lg font-medium hover:bg-brand-ink transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              返回產品列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const productName = getLocalized(product, lang, 'name');
  const productSubtitle = getLocalized(product, lang, 'subtitle');
  const productDescription = getLocalized(product, lang, 'description');
  const productCare = getLocalized(product, lang, 'careInfo');
  const productUsage = getLocalized(product, lang, 'usage');

  const tabConfig: { key: DetailTab; label: string }[] = [
    { key: 'photos', label: '相片' },
    { key: 'description', label: '詳細介紹' },
    { key: 'care', label: '產品保養' },
    { key: 'usage', label: '使用方法' },
  ];

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-20 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-base text-brand-ink/60 hover:text-brand-cinnabar"
            >
              <ChevronLeft className="w-5 h-5" />
              返回產品列表
            </Link>
          </div>

          {/* Top Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
            {/* Images */}
            <div className="space-y-4">
              <div
                className={`relative bg-white border border-brand-ink/10 flex items-center justify-center overflow-hidden cursor-zoom-in group ${
                  selectedImage && isVideoUrl(selectedImage) ? '' : 'aspect-square'
                }`}
                onClick={() => {
                  if (selectedImage && parseVideoUrl(selectedImage)?.type === 'direct') {
                    setDemoVideoUrl(selectedImage);
                  } else {
                    openLightbox(allImages.indexOf(selectedImage) || 0);
                  }
                }}
              >
                {selectedImage ? (
                  isVideoUrl(selectedImage) ? (
                    <>
                      {parseVideoUrl(selectedImage)?.type === 'direct' ? (
                        <DirectVideoPlayer url={selectedImage} className="w-full h-full bg-black pointer-events-none" />
                      ) : (
                        <VideoEmbed url={selectedImage} className="object-contain bg-black" />
                      )}
                    </>
                  ) : (
                    <>
                      <ResponsiveImage
                        src={selectedImage}
                        sizes={selectedImage === product.imageUrl ? product.imageSizes : undefined}
                        sizesAttr="(max-width: 1024px) 100vw, 50vw"
                        alt={productName}
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute bottom-4 right-4 p-3 bg-brand-ink/70 text-brand-paper opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="w-6 h-6" />
                      </div>
                    </>
                  )
                ) : (
                  <ImageIcon className="w-24 h-24 text-brand-ink/10" />
                )}
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {allImages.map((url, index) => {
                    const videoInfo = parseVideoUrl(url);
                    const thumbUrl = videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo'
                      ? videoInfo.thumbnailUrl
                      : url;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          if (parseVideoUrl(url)?.type === 'direct') {
                            setDemoVideoUrl(url);
                          } else {
                            setSelectedImage(url);
                          }
                        }}
                        className={`relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 border-2 overflow-hidden ${
                          selectedImage === url ? 'border-brand-cinnabar' : 'border-brand-ink/10'
                        }`}
                      >
                        {videoInfo && videoInfo.type !== 'direct' && thumbUrl ? (
                          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                        ) : videoInfo ? (
                          <div className="w-full h-full bg-brand-ink flex items-center justify-center">
                            <Play className="w-6 h-6 text-brand-paper" fill="currentColor" />
                          </div>
                        ) : (
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        )}
                        {videoInfo && (
                          <div className="absolute inset-0 flex items-center justify-center bg-brand-ink/20">
                            <div className="w-7 h-7 rounded-full bg-brand-ink/70 text-brand-paper flex items-center justify-center">
                              <Play className="w-3.5 h-3.5 ml-0.5" fill="currentColor" />
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <div className="text-lg text-brand-cinnabar font-medium mb-2 tracking-wide">
                {product.category || '御陽精選'}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-brand-ink mb-4 leading-tight">
                {productName}
              </h1>
              {productSubtitle && (
                <p className="text-xl md:text-2xl text-brand-ink/70 mb-6 leading-relaxed">
                  {productSubtitle}
                </p>
              )}

              <div className="flex items-baseline gap-4 mb-8">
                <span className="text-4xl md:text-5xl font-bold text-brand-cinnabar">
                  {formatPrice(effectivePrice, priceOptions)}
                </span>
                {product.comparePrice && product.comparePrice > effectivePrice && (
                  <span className="text-2xl text-brand-ink/40 line-through">
                    {formatPrice(product.comparePrice, priceOptions)}
                  </span>
                )}
              </div>

              {/* Specs */}
              {product.specs && product.specs.length > 0 && (
                <div className="bg-white border border-brand-ink/10 p-5 mb-6">
                  <h3 className="text-lg font-medium text-brand-ink mb-3">產品規格</h3>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.specs.map((spec, index) => (
                      <div key={index} className="flex justify-between border-b border-brand-ink/5 pb-2">
                        <dt className="text-brand-ink/60">{spec.labelZh}</dt>
                        <dd className="font-medium text-brand-ink">{spec.valueZh}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Variants */}
              {product.variants && product.variants.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-brand-ink mb-3">選擇規格</h3>
                  <div className="flex flex-wrap gap-3">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        className={`px-5 py-3 text-base border-2 transition-colors ${
                          selectedVariant?.id === variant.id
                            ? 'border-brand-cinnabar bg-brand-cinnabar/5 text-brand-cinnabar'
                            : 'border-brand-ink/10 bg-white text-brand-ink hover:border-brand-gold'
                        }`}
                      >
                        {getVariantName(variant, lang)}
                        {variant.priceAdjustment !== 0 && (
                          <span className="ml-2 text-sm">
                            {variant.priceAdjustment > 0 ? '+' : ''}
                            {formatPrice(variant.priceAdjustment, priceOptions)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock status */}
              <div className="mb-4 text-sm">
                {isOutOfStock ? (
                  <span className="inline-flex items-center gap-1.5 text-red-600 font-medium">
                    <span className="w-2 h-2 rounded-full bg-red-600" />
                    暫時缺貨
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-green-600 font-medium">
                    <span className="w-2 h-2 rounded-full bg-green-600" />
                    現貨：{availableStock} 件
                  </span>
                )}
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-8">
                <span className="text-lg text-brand-ink/70">數量</span>
                <div className="flex items-center border-2 border-brand-ink/10 bg-white">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={isOutOfStock}
                    className="w-14 h-14 flex items-center justify-center text-2xl text-brand-ink hover:bg-brand-stone disabled:opacity-40"
                    aria-label="減少數量"
                  >
                    <Minus className="w-6 h-6" />
                  </button>
                  <span className="w-16 text-center text-2xl font-medium text-brand-ink">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => Math.min(availableStock, q + 1))}
                    disabled={isOutOfStock}
                    className="w-14 h-14 flex items-center justify-center text-2xl text-brand-ink hover:bg-brand-stone disabled:opacity-40"
                    aria-label="增加數量"
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col items-center sm:flex-row gap-4 mb-6">
                <button
                  onClick={() => handleAddToCart(false)}
                  disabled={isOutOfStock}
                  className={`flex-1 w-full flex items-center justify-center gap-3 px-8 py-5 text-xl font-medium transition-colors ${
                    addedToCart
                      ? 'bg-green-600 text-white'
                      : isOutOfStock
                      ? 'bg-brand-ink/30 text-brand-paper cursor-not-allowed'
                      : 'bg-brand-ink text-brand-paper hover:bg-brand-cinnabar'
                  }`}
                >
                  {addedToCart ? <Check className="w-7 h-7" /> : <ShoppingCart className="w-7 h-7" />}
                  {addedToCart ? '已加入購物車' : isOutOfStock ? '暫時缺貨' : '加入購物車'}
                </button>
                <button
                  onClick={() => handleAddToCart(true)}
                  disabled={isOutOfStock}
                  className="w-full sm:w-auto flex items-center justify-center gap-3 px-12 py-4 text-xl font-medium rounded-full disabled:opacity-40 disabled:cursor-not-allowed bg-green-600 text-brand-paper hover:bg-green-700 transition-all shadow-md hover:shadow-lg"
                >
                  <CreditCard className="w-7 h-7" />
                  立即結帳
                </button>
              </div>

              <div className="flex items-center gap-6 text-brand-ink/60 text-base">
                <button className="flex items-center gap-2 hover:text-brand-cinnabar">
                  <Heart className="w-5 h-5" />
                  收藏
                </button>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: productName, url: window.location.href });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="flex items-center gap-2 hover:text-brand-cinnabar"
                >
                  <Share2 className="w-5 h-5" />
                  分享
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-ink/10 space-y-3 text-base text-brand-ink/70">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-brand-cinnabar" />
                  <span>香港本地配送</span>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-brand-cinnabar" />
                  <span>認住御陽商標，才是正宗扶陽堂產品</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-brand-ink/10 mb-8">
            <div className="flex overflow-x-auto scrollbar-hide -mb-px">
              {tabConfig.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-shrink-0 px-6 py-4 text-lg md:text-xl border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-brand-cinnabar text-brand-cinnabar bg-brand-cinnabar/5'
                      : 'border-transparent text-brand-ink/60 hover:text-brand-ink hover:bg-brand-stone/30'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-brand-ink/10 p-6 md:p-10 min-h-[300px]">
            {activeTab === 'photos' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-serif text-brand-ink mb-4">產品相片</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {allImages.map((url, index) => {
                    const videoInfo = parseVideoUrl(url);
                    return (
                      <button
                        key={index}
                        onClick={() => openLightbox(index)}
                        className={`relative overflow-hidden ${videoInfo ? 'bg-black' : 'aspect-square bg-brand-stone/30'}`}
                      >
                        {videoInfo ? (
                          <>
                            <VideoEmbed url={url} className="object-contain" controls={false} />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-12 h-12 rounded-full bg-brand-ink/60 text-brand-paper flex items-center justify-center">
                                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <img src={url} alt={`${productName} ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'description' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-serif text-brand-ink mb-6">詳細介紹</h2>
                {productDescription ? (
                  <div className="prose prose-lg max-w-none text-brand-ink/80 leading-relaxed whitespace-pre-line">
                    {productDescription}
                  </div>
                ) : (
                  <p className="text-brand-ink/50">暫無描述</p>
                )}
                {product.longImageUrl && (
                  <div className="mt-8">
                    <h3 className="text-lg font-medium text-brand-ink mb-3">產品詳情長圖</h3>
                    <button
                      onClick={() => setLongImageOpen(true)}
                      className="block w-full cursor-zoom-in"
                    >
                      <ResponsiveImage
                        src={product.longImageUrl}
                        sizes={product.longImageSizes}
                        sizesAttr="100vw"
                        alt={`${productName} 詳細介紹`}
                        className="w-full h-auto object-contain rounded-lg border border-brand-ink/10 bg-brand-paper"
                        draggable={false}
                      />
                    </button>
                    <p className="mt-2 text-sm text-brand-ink/50 text-center">點擊長圖可全屏查看</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'care' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-serif text-brand-ink mb-6">產品保養</h2>
                {productCare ? (
                  <div className="prose prose-lg max-w-none text-brand-ink/80 leading-relaxed whitespace-pre-line">
                    {productCare}
                  </div>
                ) : (
                  <p className="text-brand-ink/50">暫無保養資訊</p>
                )}
              </div>
            )}

            {activeTab === 'usage' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-serif text-brand-ink mb-6">使用方法</h2>
                {productUsage ? (
                  <div className="prose prose-lg max-w-none text-brand-ink/80 leading-relaxed whitespace-pre-line">
                    {productUsage}
                  </div>
                ) : (
                  <p className="text-brand-ink/50">暫無使用方法</p>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-brand-ink/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 p-3 text-brand-paper hover:text-brand-cinnabar"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          {allImages.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-brand-paper hover:text-brand-cinnabar"
                onClick={(e) => {
                  e.stopPropagation();
                  prevLightboxImage();
                }}
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-brand-paper hover:text-brand-cinnabar"
                onClick={(e) => {
                  e.stopPropagation();
                  nextLightboxImage();
                }}
              >
                <ChevronLeft className="w-10 h-10 rotate-180" />
              </button>
            </>
          )}
          {isVideoUrl(allImages[lightboxIndex] || '') ? (
            <div
              className="max-w-[90vw] max-h-[90vh] bg-black flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {parseVideoUrl(allImages[lightboxIndex] || '')?.type === 'direct' ? (
                <DirectVideoPlayer
                  url={allImages[lightboxIndex] || ''}
                  className="max-w-[90vw] max-h-[90vh]"
                  autoplay
                  muted
                />
              ) : (
                <VideoEmbed
                  url={allImages[lightboxIndex] || ''}
                  className="max-w-[90vw] max-h-[90vh]"
                  autoplay
                  muted
                />
              )}
            </div>
          ) : (
            <img
              src={allImages[lightboxIndex]}
              alt={productName}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-brand-paper text-lg">
            {lightboxIndex + 1} / {allImages.length || 1}
          </div>
        </div>
      )}

      {/* Demo video modal */}
      {demoVideoUrl && (
        <div
          className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setDemoVideoUrl(null)}
        >
          <button
            className="absolute top-4 right-4 p-3 text-brand-paper hover:text-brand-cinnabar z-10"
            onClick={() => setDemoVideoUrl(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div
            className="w-full max-w-4xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <VideoEmbed
              url={demoVideoUrl}
              className="w-full max-h-[90vh] bg-black"
              autoplay
              muted={false}
              controls
            />
          </div>
        </div>
      )}

      {/* Long image lightbox */}
      {longImageOpen && product.longImageUrl && (
        <div
          className="fixed inset-0 z-[120] bg-brand-ink/95 overflow-y-auto"
          onClick={() => setLongImageOpen(false)}
        >
          <button
            className="fixed top-4 right-4 p-3 text-brand-paper hover:text-brand-cinnabar z-10"
            onClick={() => setLongImageOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="min-h-screen flex items-start justify-center p-4 sm:p-8">
            <ResponsiveImage
              src={product.longImageUrl}
              sizes={product.longImageSizes}
              sizesAttr="100vw"
              alt={`${productName} 詳細介紹`}
              className="w-full max-w-4xl h-auto object-contain"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />
          </div>
        </div>
      )}

      <Footer />
      <FloatingActions />
    </div>
  );
};

export default ProductDetailPage;
